import { useMemo, useState } from 'react';
import { AppShell, PageTitle } from '@/components/layout';
import { Button, SearchInput, Select } from '@/components/forms';
import { Icon, PixIcon } from '@/components/foundations';
import { DataTable, KeyValueList, PAYMENT_STATUS, Pagination, StatusBadge, TableFilters, Timeline } from '@/components/data';
import { DetailDrawer, DrawerLayout, DrawerSection, StatusHero } from '@/components/feedback';
import { MERCHANT_TX, type MerchantTx } from '@/mocks/merchant';

function Method({ t, inline = false }: { t: MerchantTx; inline?: boolean }) {
  if (t.kind === 'pix')
    return inline ? (
      <>
        <span style={{ display: 'inline-flex', width: 20 }}>
          <PixIcon />
        </span>{' '}
        Pix
      </>
    ) : (
      <span className="method">
        <span className="mi" style={{ background: 'none' }}>
          <PixIcon />
        </span>
        Pix
      </span>
    );
  return inline ? (
    <>
      <Icon name="card" /> Cartão •••• {t.last}
    </>
  ) : (
    <span className="method">
      <span className="mi" style={{ background: 'none' }}>
        <Icon name="card" />
      </span>
      Cartão •••• {t.last}
    </span>
  );
}

function exportCsv(rows: MerchantTx[]) {
  const csv =
    'id;cliente;metodo;status;data;valor\n' +
    rows.map((t) => [t.id, t.cli, t.kind, PAYMENT_STATUS[t.st].label, t.date, t.val].join(';')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = 'transacoes.csv';
  a.click();
}

/** Transações com filtros e painel de detalhes — fiel a design/html/transactions.html (mock). */
export function TransactionsPage() {
  const [sel, setSel] = useState<number | null>(0);
  const [q, setQ] = useState('');
  const [st, setSt] = useState('');
  const [m, setM] = useState('');
  const visible = useMemo(
    () =>
      MERCHANT_TX.map((t, i) => ({ t, i })).filter(
        ({ t }) =>
          (!q || (t.cli + t.id).toLowerCase().includes(q.toLowerCase().replace('#', ''))) &&
          (!st || t.st === st) &&
          (!m || t.kind === m),
      ),
    [q, st, m],
  );
  const t = sel === null ? null : MERCHANT_TX[sel];

  return (
    <AppShell stackHelp>
      <DrawerLayout
        open={!!t}
        drawer={
          <DetailDrawer title="Detalhes da transação" open={!!t} onClose={() => setSel(null)}>
            {t && (
              <>
                <StatusHero tone={t.st} label={PAYMENT_STATUS[t.st].title} icon={PAYMENT_STATUS[t.st].icon} amount={t.val} />
                <DrawerSection icon="user" title="Informações do cliente">
                  <div className="ind"><p>{t.cli}</p><p className="m">{t.mail}</p></div>
                </DrawerSection>
                <DrawerSection icon="card" title="Informações do pagamento">
                  <KeyValueList
                    items={[
                      { k: 'ID da transação', v: t.txn, mono: true },
                      { k: 'Método de pagamento', v: <Method t={t} inline />, mono: true },
                      { k: 'Valor', v: t.val, mono: true },
                      { k: 'Taxa', v: t.fee, mono: true },
                      { k: 'Valor líquido', v: t.net, mono: true },
                    ]}
                  />
                </DrawerSection>
                <DrawerSection icon="clock" title="Linha do tempo">
                  <Timeline events={t.tl.map(([title, time, ok]) => ({ title, time, ok: !!ok }))} />
                </DrawerSection>
                <Button variant="outline-navy" block href="#" icon="ext">Ver no Asaas</Button>
              </>
            )}
          </DetailDrawer>
        }
      >
          <PageTitle
            variant="page"
            size="lg"
            title="Transações"
            subtitle="Acompanhe todos os pagamentos da sua loja."
            actions={<Button size="lg" icon="download" onClick={() => exportCsv(MERCHANT_TX)}>Exportar</Button>}
          />
          <TableFilters>
            <SearchInput label="Buscar por cliente ou ID" placeholder="Buscar por cliente ou ID" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select label="Período" defaultValue="" placeholder="Período" options={['Últimos 7 dias', 'Este mês', 'Mês anterior'].map((v) => ({ value: v, label: v }))} />
            <Select label="Status" value={st} onChange={(e) => setSt(e.target.value)} placeholder="Status" options={[{ value: 'ok', label: 'Aprovado' }, { value: 'warn', label: 'Pendente' }, { value: 'err', label: 'Recusado' }]} />
            <Select label="Método de pagamento" value={m} onChange={(e) => setM(e.target.value)} placeholder="Método de pagamento" options={[{ value: 'pix', label: 'Pix' }, { value: 'card', label: 'Cartão de crédito' }]} />
          </TableFilters>
          <section className="card tx-table">
            <DataTable
              rows={visible}
              rowKey={({ t: r }) => r.id}
              onRowClick={({ i }) => setSel(i)}
              isSelected={({ i }) => sel === i}
              columns={[
                { key: 'id', header: 'Transação', cellClassName: 'id', render: ({ t: r }) => <>#{r.id}</> },
                { key: 'cli', header: 'Cliente', render: ({ t: r }) => r.cli },
                { key: 'method', header: 'Método', render: ({ t: r }) => <Method t={r} /> },
                { key: 'st', header: 'Status', render: ({ t: r }) => <StatusBadge status={r.st} /> },
                { key: 'date', header: 'Data', render: ({ t: r }) => r.date },
                { key: 'val', header: 'Valor', cellClassName: 'val', render: ({ t: r }) => r.val },
                { key: 'go', header: '', align: 'right', render: () => <Icon name="chev" className="chev-c" /> },
              ]}
            />
            <Pagination page={1} pageCount={1} summary={visible.length ? `Mostrando 1 a ${visible.length} de ${visible.length} transações` : 'Nenhuma transação encontrada'} />
          </section>
      </DrawerLayout>
    </AppShell>
  );
}
